import { useEffect, useMemo, useRef, useState } from 'react'
import { searchLocations } from '../services/geocodeService'
import { matchStores, asSuggestion, houseNumberIn } from '../data/stores'
import { readPlaces, renamePlace, forgetPlace } from '../utils/places'
import { coords as formatCoords } from '../utils/helpers'

/**
 * A place this browser has been before, in the shape the suggestion list already speaks.
 *
 * It goes through `selectSuggestion` like a listed shop or a Nominatim row, so choosing one
 * is a state update and no network — there is no third path through this hook.
 *
 * The caption is worth the line. Without it the rows are unexplained: three addresses appear
 * under a box nobody has typed into yet, and the customer has to work out where they came
 * from. It sits inside the row that already exists, not in a section of its own.
 */
const asRemembered = (place, kind) => ({
  lat: place.lat,
  lng: place.lng,
  /**
   * What the customer calls it, which for a shop is its name.
   *
   * A pickup row is identified by the shop — that is what fills "Pangalan ng tindahan" and
   * what the rider asks for at the counter — so the name leads and the address is only the
   * fallback. A dropoff has no name of its own; its label is the whole answer.
   *
   * A place saved from "gamitin ang lokasyon ko ngayon" keeps neither, on purpose, and the
   * coordinates stand in — the same fallback the step itself uses.
   */
  label: (kind === 'pickup' ? place.name || place.label : place.label)
    || formatCoords(place.lat, place.lng),
  secondary: 'Dati mong ginamit',
  name: place.name,
  /**
   * What tells this row apart from a shop or a Nominatim result.
   *
   * Only a remembered place can be renamed or forgotten — the other two are somebody
   * else's data. The picker reads this to decide whether the row carries its two extra
   * controls.
   */
  remembered: true,
  /** The unedited record, so a rename is matched on the pin rather than on the label. */
  place,
})

/**
 * The debounced place search for one location, pickup or dropoff.
 *
 * The caller owns the location's state (query / lat / lng / label) and passes it in with
 * a setter.
 *
 * It used to carry a "paste a map link" flow as well — parse the coordinates out of a
 * Google Maps URL, then reverse-geocode them back into a street. That is gone. It was two
 * controls and a second mental model on a step whose whole premise is having one thing on
 * it, and dropping a pin on the full map answers the same question without asking the
 * customer to find and copy a URL first.
 */
export function useLocationSearch(loc, setLoc, kind) {
  const [suggestions, setSuggestions] = useState(null) // null = hidden, [] = "no matches", 'error', or [...]
  const debounceRef = useRef(null)
  /**
   * The in-flight request, so a fourth keystroke cancels the third one's answer.
   *
   * Without it a slow response can land after a faster later one and overwrite the list
   * with results for a query the customer has already typed past.
   */
  const abortRef = useRef(null)

  /**
   * Bumped by a rename or a forget, and by nothing else.
   *
   * The list is otherwise read once per step, not per keystroke: it only changes when an
   * order is placed, and no order is placed while this screen is open. Re-reading and
   * re-parsing `localStorage` on every character typed would be work done on the slowest
   * phones for an answer that cannot have moved.
   *
   * `localStorage` stays the record. Mirroring it into state would let the two disagree the
   * first time anything else writes — an order placed in another tab, a sign-out — and the
   * box would be offering a place that no longer exists.
   */
  const [edits, setEdits] = useState(0)

  const remembered = useMemo(
    () => readPlaces(kind).map((place) => asRemembered(place, kind)),
    [kind, edits],
  )

  useEffect(() => {
    return () => {
      clearTimeout(debounceRef.current)
      abortRef.current?.abort()
    }
  }, [])

  /**
   * What to show when there is nothing to search on.
   *
   * The empty box used to answer with `null` — no list at all. That was right when there was
   * nothing to put there; now there is, and it is the answer for most customers most of the
   * time. Still `null` when nothing is remembered, so a first-time customer sees exactly the
   * screen they see today.
   *
   * This is also the way back from the pin that typing lets go of, above. Without it, a
   * customer who typed one character over their seeded dropoff would have to search for
   * their own house to get it back.
   */
  function showRemembered() {
    clearTimeout(debounceRef.current)
    abortRef.current?.abort()

    setSuggestions(remembered.length > 0 ? remembered : null)
  }

  /**
   * Typing lets go of the pin.
   *
   * `validateStep` asks only whether a pin exists (`validation.js`), so a box whose text has
   * been typed over while an older pin is still attached passes the step and sends the
   * *older* address — the customer reads their new answer on the screen and the rider rides
   * to the previous one. The two must not be allowed to disagree.
   *
   * Reachable before this feature, but only by choosing a place and then editing the text.
   * A seeded dropoff makes it reachable in the first second of the step, on the screen that
   * opens already answered, which is why it is fixed here.
   *
   * An unchanged string keeps the pin: React fires change events that do not change
   * anything, and dropping a confirmed answer on one of those would be its own bug.
   */
  function handleQueryChange(value) {
    setLoc((prev) => (
      prev.lat != null && String(value) !== String(prev.label)
        ? { ...prev, query: value, lat: null, lng: null, label: '' }
        : { ...prev, query: value }
    ))

    clearTimeout(debounceRef.current)
    abortRef.current?.abort()

    // Answered here rather than after the debounce: there is no request to wait for, and a
    // list that arrives 400ms after the box empties reads as the screen being slow.
    if (String(value).trim().length < 3) {
      setSuggestions(remembered.length > 0 ? remembered : null)
      return
    }

    debounceRef.current = setTimeout(() => {
      abortRef.current = new AbortController()
      doGeocode(value, abortRef.current.signal)
    }, 400)
  }

  /**
   * The shop list first, Nominatim for everything else.
   *
   * The list is 537 real shops in Los Baños and Bay — every eatery, pharmacy, bakery,
   * hardware, sari-sari and palengke OpenStreetMap knows about. It is not a screen; nobody
   * scrolls that. It sits behind the box and surfaces only what somebody typed towards.
   *
   * **First because it is instant and free.** No round trip, no rate limit, no key. In a
   * town this size the same forty shops are searched over and over, and those are answered
   * before anything leaves the browser.
   *
   * **Exclusive, not merged.** A query naming a listed shop returns that shop and stops.
   * Mixing Nominatim's rows underneath would bury the right answer in near-misses and ask
   * the customer to tell them apart.
   *
   * Nominatim answers the rest — a street, a landmark, a barangay. It is scoped to the same
   * two municipalities, so it cannot offer somewhere the backend will refuse.
   *
   * Google Places sat between these two for a while and was removed. It searched better,
   * and it cost a call against a monthly cap for every keystroke past the list — which the
   * list had already made rare. The better search was paying for the queries it was worst
   * at helping with.
   */
  async function doGeocode(query, signal) {
    if (!query || query.trim().length < 3) {
      setSuggestions(null)
      return
    }

    const shops = matchStores(query)

    if (shops.length > 0) {
      setSuggestions(shops.map(asSuggestion))
      return
    }

    try {
      const results = await searchLocations(query)
      setSuggestions(results)
    } catch (err) {
      setSuggestions('error')
    }
  }

  /**
   * A listed shop and a Nominatim row both arrive here carrying their own coordinates.
   *
   * There is no second request any more. Places suggestions were a place id and nothing
   * else — the pin had to be fetched, which is what ended the billable session — and with
   * that gone, choosing a place is a state update and no network at all.
   */
  function selectSuggestion(s) {
    setLoc((prev) => {
      /**
       * The house number the customer typed, put back on the answer.
       *
       * `9157 Emerald Street` is how an address is written and *Emerald Street* is all any
       * listing can hold — no geocoder on earth has house 9157 in a sitio. `matchIn` finds
       * the street by dropping the number; this is the other half, and without it the
       * number is silently thrown away and the rider is sent to a street with no door.
       *
       * Only onto a street or a subdivision. A number in front of a shop name is part of
       * the name — *7-Eleven*, *Petron 2* — and prefixing one would invent an address.
       *
       * The pin does not move: it is still the street's, and it was never the house. What
       * changes is what the partner's screen and the rider's phone read.
       */
      const number = ['street', 'village'].includes(s.store?.category)
        ? houseNumberIn(prev.query)
        : ''

      const label = number ? `${number} ${s.label}` : s.label

      return { ...prev, lat: s.lat, lng: s.lng, label, query: label }
    })
    setSuggestions(null)
  }

  /**
   * Rename a remembered place from the list it is sitting in.
   *
   * The label is what the partner's screen shows and what the rider is read out at the
   * gate, and it arrives as whatever answered the search — *"Batong Malake, Los Baños"*, or
   * a line of coordinates for a hand-dropped pin. Neither is what the person living there
   * calls it.
   *
   * Matched on the pin, because the label is the thing being changed. Blank is allowed and
   * means *no name*: the row falls back to the coordinates, where it started.
   */
  function renameRemembered(place, label) {
    renamePlace(kind, place, label)
    refresh()
  }

  /**
   * Forget one, without waiting four deliveries for it to fall off the end.
   *
   * The list holds five and fills itself from real orders, so an address somebody has moved
   * away from is otherwise offered on every screen until four other places push it out.
   */
  function forgetRemembered(place) {
    forgetPlace(kind, place)
    refresh()
  }

  /**
   * Re-read the store, and put the result on screen if that is what is on screen.
   *
   * The bump alone is not enough. `suggestions` holds the array that was handed to the list
   * when it opened, so after an edit it would keep rendering the old label until something
   * else replaced it — the customer would rename a place and watch nothing happen.
   *
   * Guarded, because these two are reachable only from the remembered list but the list is
   * not the only thing `suggestions` ever holds.
   */
  function refresh() {
    const next = readPlaces(kind).map((place) => asRemembered(place, kind))

    setEdits((count) => count + 1)
    setSuggestions((shown) => (
      Array.isArray(shown) && shown.every((row) => row.remembered)
        ? (next.length ? next : null)
        : shown
    ))
  }

  return {
    suggestions,
    handleQueryChange,
    selectSuggestion,
    showRemembered,
    renameRemembered,
    forgetRemembered,
  }
}
